import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, Loader2, Pill } from 'lucide-react';
import type { MarketStoryline, StorylineChartEvent, StorylineChartSymbol } from '@/mocks/marketStorylinesMock';
import { formatDateShort, formatPct, pctChipClass } from '@/components/AnalogLensAccordion';
import { Glp1EventStockCharts, type Glp1HistoricalPriceRow } from '@/components/Glp1EventStockCharts';
import {
  storylineChartEventsService,
  stockService,
  type Glp1StorylineChartEvent,
} from '@/services/api';

type Props = {
  storyline: MarketStoryline;
  className?: string;
};

type TickerMove = {
  symbol: string;
  label: string;
  percentChange: number | null;
};

export type Glp1TimelineEvent = StorylineChartEvent & {
  tickerMoves: TickerMove[];
  hasResearch: boolean;
};

const PRIMARY_PCT_SYMBOLS = ['OZEM', 'LLY', 'NVO', 'SIXV'] as const;
const PRICE_HISTORY_DAYS = 1825;

const SENTIMENT_BADGE: Record<
  StorylineChartEvent['sentiment'],
  { label: string; className: string }
> = {
  positive: { label: 'Positive', className: 'bg-emerald-500/10 text-emerald-700' },
  negative: { label: 'Negative', className: 'bg-rose-500/10 text-rose-700' },
  neutral: { label: 'Neutral', className: 'bg-gray-500/10 text-gray-600' },
};

function toChartEvent(row: Glp1StorylineChartEvent): StorylineChartEvent {
  return {
    date: row.date,
    label: row.label,
    summary: row.summary,
    sentiment: row.sentiment,
    percentChange: row.percentChange,
    primaryCatalyst: row.primaryCatalyst,
    glp1Context: row.glp1Context,
    citations: row.citations,
  };
}

function pickPrimaryPercentChange(moves: TickerMove[]): number | null {
  for (const symbol of PRIMARY_PCT_SYMBOLS) {
    const row = moves.find((move) => move.symbol === symbol);
    if (row?.percentChange != null) return row.percentChange;
  }
  return moves.find((move) => move.percentChange != null)?.percentChange ?? null;
}

function mergeTimelineEvents(
  storyline: MarketStoryline,
  eventsBySymbol: Record<string, Glp1StorylineChartEvent[]>,
  tickerLabels: Record<string, string>,
): Glp1TimelineEvent[] {
  const catalog = storyline.events;
  const symbols =
    Object.keys(eventsBySymbol).length > 0
      ? Object.keys(eventsBySymbol)
      : (storyline.chartSymbols ?? []).map((chart) => chart.symbol);

  return catalog
    .map((catalogEvent) => {
      const tickerMoves: TickerMove[] = symbols.map((symbol) => {
        const rows = eventsBySymbol[symbol] ?? [];
        const match = rows.find(
          (row) => row.date === catalogEvent.date && row.label === catalogEvent.label,
        );
        return {
          symbol,
          label: tickerLabels[symbol] ?? symbol,
          percentChange: match?.percentChange ?? null,
        };
      });

      const enrichedRows = Object.values(eventsBySymbol)
        .flat()
        .filter((row) => row.date === catalogEvent.date && row.label === catalogEvent.label);

      const preferred =
        enrichedRows.find((row) =>
          row.symbol && PRIMARY_PCT_SYMBOLS.includes(row.symbol as (typeof PRIMARY_PCT_SYMBOLS)[number]),
        ) ?? enrichedRows[0];

      const merged: Glp1TimelineEvent = {
        ...catalogEvent,
        ...(preferred ? toChartEvent(preferred) : {}),
        percentChange: pickPrimaryPercentChange(tickerMoves) ?? catalogEvent.percentChange ?? null,
        tickerMoves,
        hasResearch: Boolean(preferred),
      };
      return merged;
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

function Glp1EventRow({
  event,
  rank,
  charts,
  pricesBySymbol,
  eventsBySymbol,
  catalogEvents,
  pricesLoading,
  isOpen,
  onToggle,
}: {
  event: Glp1TimelineEvent;
  rank: number;
  charts: StorylineChartSymbol[];
  pricesBySymbol: Record<string, Glp1HistoricalPriceRow[]>;
  eventsBySymbol: Record<string, Glp1StorylineChartEvent[]>;
  catalogEvents: StorylineChartEvent[];
  pricesLoading: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const sentiment = SENTIMENT_BADGE[event.sentiment];
  const headline = event.primaryCatalyst
    ? `${event.label} — ${event.primaryCatalyst}`
    : `${event.label} — ${event.summary}`;

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 md:px-5 ${
          isOpen ? 'bg-gray-50' : ''
        }`}
      >
        <span className="w-5 shrink-0 text-xs font-medium text-gray-400">{rank}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium text-gray-900">{formatDateShort(event.date)}</span>
            {event.percentChange != null ? (
              <span className={`text-sm font-medium ${pctChipClass(event.percentChange)}`}>
                {formatPct(event.percentChange)}
              </span>
            ) : null}
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${sentiment.className}`}>
              {sentiment.label}
            </span>
            {event.hasResearch ? (
              <span className="rounded bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                Exa research
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{headline}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 bg-white px-2 py-3 md:px-3">
              <Glp1EventStockCharts
                event={event}
                charts={charts}
                pricesBySymbol={pricesBySymbol}
                eventsBySymbol={eventsBySymbol}
                catalogEvents={catalogEvents}
                pricesLoading={pricesLoading}
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function Glp1StorylineEventsPanel({ storyline, className = '' }: Props) {
  const charts = storyline.chartSymbols ?? [];
  const [loading, setLoading] = useState(true);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [eventsBySymbol, setEventsBySymbol] = useState<Record<string, Glp1StorylineChartEvent[]>>({});
  const [pricesBySymbol, setPricesBySymbol] = useState<Record<string, Glp1HistoricalPriceRow[]>>({});
  const [tickerLabels, setTickerLabels] = useState<Record<string, string>>({});
  const [expandedEventKey, setExpandedEventKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPrices = async () => {
      setPricesLoading(true);
      const fetchSymbols = Array.from(
        new Set(charts.map((chart) => chart.priceSymbol ?? chart.symbol)),
      );
      const entries = await Promise.all(
        fetchSymbols.map(async (symbol) => {
          const rows = await stockService.getHistoricalPrices(symbol, PRICE_HISTORY_DAYS);
          const mapped: Glp1HistoricalPriceRow[] = (rows ?? [])
            .filter((row) => row.date && Number.isFinite(row.close))
            .map((row) => ({
              date: row.date,
              close: row.close,
              percent_change: row.percent_change,
            }))
            .sort((a, b) => a.date.localeCompare(b.date));
          return [symbol, mapped] as const;
        }),
      );
      if (cancelled) return;
      setPricesBySymbol(Object.fromEntries(entries));
      setPricesLoading(false);
    };

    const loadEvents = async () => {
      setLoading(true);
      try {
        const payload = await storylineChartEventsService.getGlp1DisruptionEvents();
        if (cancelled) return;
        setEventsBySymbol(payload?.eventsBySymbol ?? {});
        const labels: Record<string, string> = {};
        for (const ticker of payload?.tickers ?? []) {
          labels[ticker.symbol] = ticker.label;
        }
        for (const chart of charts) {
          labels[chart.symbol] = chart.label;
        }
        setTickerLabels(labels);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void Promise.all([loadEvents(), loadPrices()]);
    return () => {
      cancelled = true;
    };
  }, [charts]);

  const timelineEvents = useMemo(
    () => mergeTimelineEvents(storyline, eventsBySymbol, tickerLabels),
    [storyline, eventsBySymbol, tickerLabels],
  );

  const handleToggle = (eventKey: string) => {
    setExpandedEventKey((prev) => (prev === eventKey ? null : eventKey));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={`overflow-hidden rounded-3xl border border-gray-200 bg-white ${className}`}
    >
      <div className="border-b border-gray-100 px-6 py-5 md:px-8 md:py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-900">
                <Pill className="h-4 w-4 text-white" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                GLP-1 Catalyst Timeline
              </span>
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">{storyline.title}</h3>
            <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-500">{storyline.summary}</p>
          </div>
          <span className="rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600">
            {timelineEvents.length} events
          </span>
        </div>
      </div>

      <div className="border-t border-gray-100">
        {loading ? (
          <div className="flex items-center gap-2 px-6 py-10 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading GLP-1 catalyst research…
          </div>
        ) : (
          <div>
            {timelineEvents.map((event, index) => {
              const eventKey = `${event.date}::${event.label}`;
              return (
                <Glp1EventRow
                  key={eventKey}
                  event={event}
                  rank={index + 1}
                  charts={charts}
                  pricesBySymbol={pricesBySymbol}
                  eventsBySymbol={eventsBySymbol}
                  catalogEvents={storyline.events}
                  pricesLoading={pricesLoading}
                  isOpen={expandedEventKey === eventKey}
                  onToggle={() => handleToggle(eventKey)}
                />
              );
            })}
          </div>
        )}
      </div>
    </motion.section>
  );
}
